import type {
  SessionTimeoutConfig,
  TokenRefreshConfig,
} from '#/modules/mes/system/config/types/session';

import { computed, ref, watch } from 'vue';

import { useAccessStore } from '@vben/stores';

import { useIdle } from '@vueuse/core';
import { Modal, notification } from 'ant-design-vue';

import { refreshAccessToken } from '#/api/request';
import { $t } from '#/locales';
import {
  DEFAULT_SESSION_CONFIG,
  DEFAULT_TOKEN_REFRESH_CONFIG,
  SessionExpiredMode,
} from '#/modules/mes/system/config/types/session';
import { useAuthStore } from '#/store';

/**
 * 全局标记：防止重复初始化
 * 使用 Symbol 确保唯一性，存储在 window 对象避免热更新重置
 */
const SESSION_TIMEOUT_KEY = Symbol.for('__SESSION_TIMEOUT_INITIALIZED__');

/**
 * Token 状态存储 Key
 */
const TOKEN_STATE_KEY = Symbol.for('__TOKEN_REFRESH_STATE__');

/**
 * localStorage 存储 Key
 */
const TOKEN_CREATED_AT_STORAGE_KEY = 'token_created_at';

/**
 * Token 刷新状态
 */
interface TokenRefreshState {
  /** Token 创建时间戳 */
  tokenCreatedAt: number;
  /** Token 有效期（毫秒） */
  tokenExpiresIn: number;
  /** 刷新窗口（毫秒） */
  refreshWindowMs: number;
  /** 是否正在刷新中（防止并发刷新） */
  isRefreshing: boolean;
}

/**
 * 从 localStorage 读取 Token 创建时间
 */
function loadTokenCreatedAt(): number {
  try {
    const stored = localStorage.getItem(TOKEN_CREATED_AT_STORAGE_KEY);
    if (stored) {
      const timestamp = Number.parseInt(stored, 10);
      if (!Number.isNaN(timestamp) && timestamp > 0) {
        return timestamp;
      }
    }
  } catch (error) {
    console.warn('[Token刷新] 读取 localStorage 失败:', error);
  }
  return Date.now();
}

/**
 * 保存 Token 创建时间到 localStorage
 */
function saveTokenCreatedAt(timestamp: number): void {
  try {
    localStorage.setItem(TOKEN_CREATED_AT_STORAGE_KEY, String(timestamp));
  } catch (error) {
    console.warn('[Token刷新] 保存到 localStorage 失败:', error);
  }
}

/**
 * 清除 localStorage 中的 Token 创建时间
 */
export function clearTokenCreatedAt(): void {
  try {
    localStorage.removeItem(TOKEN_CREATED_AT_STORAGE_KEY);
  } catch (error) {
    console.warn('[Token刷新] 清除 localStorage 失败:', error);
  }
}

/**
 * 获取或初始化 Token 状态
 */
function getTokenState(): TokenRefreshState {
  if (!(window as any)[TOKEN_STATE_KEY]) {
    // 从 localStorage 恢复 Token 创建时间
    const storedCreatedAt = loadTokenCreatedAt();

    (window as any)[TOKEN_STATE_KEY] = {
      tokenCreatedAt: storedCreatedAt,
      tokenExpiresIn: DEFAULT_TOKEN_REFRESH_CONFIG.tokenExpiresIn * 60 * 1000,
      refreshWindowMs:
        DEFAULT_TOKEN_REFRESH_CONFIG.refreshWindowMinutes * 60 * 1000,
      isRefreshing: false,
    };

    if (import.meta.env.DEV) {
      const age = Date.now() - storedCreatedAt;
      const ageMinutes = Math.floor(age / 1000 / 60);
      // eslint-disable-next-line no-console
      console.log(
 '%c[Token刷新] 状态已恢复',
        'color: #1890ff; font-weight: bold;',
        `Token已使用: ${ageMinutes}分钟`,
      );
    }
  }
  return (window as any)[TOKEN_STATE_KEY];
}

/**
 * 更新 Token 创建时间（登录或刷新后调用）
 */
export function updateTokenCreatedAt(timestamp: number = Date.now()): void {
  const state = getTokenState();
  state.tokenCreatedAt = timestamp;
  state.isRefreshing = false;

  // 持久化到 localStorage
  saveTokenCreatedAt(timestamp);

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(
      '%c[Token刷新] ✓ Token时间已更新并持久化',
      'color: #52c41a; font-weight: bold;',
      new Date(timestamp).toLocaleTimeString(),
    );
  }
}

/**
 * 更新 Token 配置
 */
export function updateTokenConfig(
  config: Partial<Omit<TokenRefreshConfig, 'tokenCreatedAt'>>,
): void {
  const state = getTokenState();
  if (config.tokenExpiresIn !== undefined) {
    state.tokenExpiresIn = config.tokenExpiresIn * 60 * 1000;
  }
  if (config.refreshWindowMinutes !== undefined) {
    state.refreshWindowMs = config.refreshWindowMinutes * 60 * 1000;
  }
}

/**
 * 判断是否需要刷新 Token
 *
 * @description 方案B核心逻辑：只在快过期时才刷新
 *
 * | Token 剩余时间    | 是否刷新 |
 * |------------------|---------|
 * | > 刷新窗口(5分钟) | ❌ 不刷新 |
 * | < 刷新窗口(5分钟) | ✅ 刷新   |
 */
export function shouldRefreshToken(): boolean {
  const state = getTokenState();

  // 只检查本地主动刷新状态；被动刷新（401拦截器）与主动刷新的并发
  // 由 request.ts 的模块级共享刷新锁（refreshAccessToken）统一串行化
  if (state.isRefreshing) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
 '%c[Token刷新] 跳过：已有主动刷新进行中',
        'color: #fa8c16;',
      );
    }
    return false;
  }

  const tokenAge = Date.now() - state.tokenCreatedAt;
  const remainingTime = state.tokenExpiresIn - tokenAge;
  const remainingMinutes = Math.floor(remainingTime / 1000 / 60);
  const refreshWindowMinutes = Math.floor(state.refreshWindowMs / 1000 / 60);

  // 只有剩余时间 < 刷新窗口 才需要刷新
  const shouldRefresh =
    remainingTime < state.refreshWindowMs && remainingTime > 0;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(
 `%c[Token刷新] 检查状态: 剩余${remainingMinutes}分钟, 刷新窗口${refreshWindowMinutes}分钟, 需要刷新: ${shouldRefresh ? '是' : '否'}`,
      shouldRefresh ? 'color: #fa8c16; font-weight: bold;' : 'color: #8c8c8c;',
    );
  }

  return shouldRefresh;
}

/**
 * 执行 Token 刷新
 *
 * @description 在用户活动时调用，如果 Token 快过期则刷新
 *
 * 关键修复（v3.0）：
 * - 刷新统一走 request.ts 的 refreshAccessToken()（模块级共享锁 + 等待队列），
 *   与 requestClient/uploadClient 等五个客户端的 401 拦截器被动刷新串行化，
 *   杜绝并发刷新触发后端一次性轮换的 token 被删 → 401 → 被踢回登录页
 */
async function doRefreshTokenIfNeeded(): Promise<void> {
  if (!shouldRefreshToken()) {
    return;
  }

  const state = getTokenState();
  const accessStore = useAccessStore();

  // 如果没有 Token，不刷新
  if (!accessStore.accessToken) {
    return;
  }

  state.isRefreshing = true;

  try {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
 '%c[Token刷新] 开始刷新Token（主动模式）...',
        'color: #1890ff; font-weight: bold;',
      );
    }

    // 共享刷新锁内部会去重：若 401 拦截器已在刷新，这里会排队等待新 token
    await refreshAccessToken();

    // 同步本地会话状态（重置 state.isRefreshing 并刷新时间戳）
    updateTokenCreatedAt();

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
        '%c[Token刷新] ✅ Token刷新成功（主动模式）',
        'color: #52c41a; font-weight: bold;',
      );
    }
  } catch (error) {
    console.error('[Token刷新] ❌ Token刷新失败:', error);
    state.isRefreshing = false;
    // 刷新失败不影响用户操作，等待下次尝试或 401 拦截器处理
  }
}

/**
 * 检查是否已初始化
 */
function isInitialized(): boolean {
  return (window as any)[SESSION_TIMEOUT_KEY] === true;
}

/**
 * 设置初始化状态
 */
function setInitialized(value: boolean): void {
  (window as any)[SESSION_TIMEOUT_KEY] = value;
}

/**
 * 会话超时检测 Hook
 *
 * 功能：
 * - 监听用户活动（鼠标移动、键盘输入、点击等）
 * - 指定时间内无操作自动注销
 * - 提前5分钟弹窗提示用户
 * - 支持用户主动延长会话
 *
 * 工作原理：
 * 1. 使用 @vueuse/core 的 useIdle 监听用户活动
 * 2. 在即将超时前5分钟显示警告
 * 3. 超时后自动注销并清除Token
 * 4. 用户可以通过任何操作延长会话
 *
 * 安全说明：
 * - 防止未经授权的人员使用遗留的登录会话
 * - 符合等保2.0要求（会话超时自动注销）
 * - 与后端会话超时机制配合工作
 *
 * @author YSFL Development Team
 * @since 2025-10-05
 *
 * @example
 * ```typescript
 * // 在应用根组件中使用
 * import { useSessionTimeout } from '#/composables/useSessionTimeout';
 *
 * // 启用30分钟会话超时
 * useSessionTimeout(30);
 * ```
 */

/**
 * 启用会话超时检测（内部实现）
 *
 * @param config 配置选项
 */
function initSessionTimeout(config: SessionTimeoutConfig) {
  const authStore = useAuthStore();
  const accessStore = useAccessStore();

  const {
    timeoutMinutes = 30,
    warningMinutes = 5,
    enabled = true,
    expiredMode = SessionExpiredMode.MODAL,
  } = config;

  // 如果禁用，直接返回
  if (!enabled) {
    return;
  }

  // 转换为毫秒
  const timeoutMs = timeoutMinutes * 60 * 1000;

  // 使用 @vueuse/core 的 useIdle 监听用户活动
  // idle 为 true 表示用户在指定时间内没有任何操作
  // lastActive 为上次活动的时间戳
  //
  // 配置监听的事件：
  // - mousemove: 鼠标移动
  // - mousedown: 鼠标按下
  // - keydown: 键盘按下
  // - touchstart: 触摸开始（移动设备）
  // - wheel: 鼠标滚轮
  // - visibilitychange: 页面可见性变化
  const { idle, lastActive } = useIdle(timeoutMs, {
    events: ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'],
    initialState: false, // 初始状态为活跃（非空闲）
  });

  // 是否已经显示过警告
  let hasWarned = false;

  // 警告弹窗的引用（用于关闭）
  let warningModal: null | ReturnType<typeof Modal.warning> = null;

  // 定时器引用（用于清理）
  let debugIntervalId: null | number = null;
  let updateIntervalId: null | number = null;

  // 使用 ref 而不是 computed，以便通过定时器更新
  const currentTime = ref(Date.now());

  // 计算剩余时间（秒）
  const remainingSeconds = computed(() => {
    if (!lastActive.value) return timeoutMinutes * 60;

    const elapsed = currentTime.value - lastActive.value;
    const remaining = Math.max(0, timeoutMs - elapsed);

    return Math.floor(remaining / 1000);
  });

  // 格式化剩余时间为 "MM:SS"
  const formattedRemainingTime = computed(() => {
    const seconds = remainingSeconds.value;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  });

  // 显示警告提示
  function showWarning() {
    if (hasWarned || warningModal) return;

    hasWarned = true;

    warningModal = Modal.warning({
      title: $t('app.session.warningTitle'),
      content: $t('app.session.warningContent', {
        elapsed: timeoutMinutes - warningMinutes,
        remaining: warningMinutes,
      }),
      okText: $t('app.session.warningOk'),
      onOk: () => {
        warningModal = null;
      },
    });
  }

  // 自动注销
  async function autoLogout() {
    // 关闭警告弹窗（如果存在）
    if (warningModal) {
      warningModal.destroy();
      warningModal = null;
    }

    // 根据配置的超时模式处理
    if (expiredMode === SessionExpiredMode.MODAL) {
      // 弹窗模式：显示登录过期弹窗，由框架的 AuthenticationLoginExpiredModal 处理
      console.warn('会话已超时，显示登录过期弹窗', new Date());

      // 清除Token
      accessStore.setAccessToken(null);

      // 设置登录过期标记，触发框架弹窗
      accessStore.setLoginExpired(true);
    } else {
      // 重定向模式：显示通知并跳转到登录页
      notification.warning({
        message: $t('app.session.expiredTitle'),
        description: $t('app.session.expiredContent', {
          timeout: timeoutMinutes,
        }),
        duration: 5,
      });

      // 执行注销并跳转到登录页
      try {
        await authStore.logout(true); // 强制跳转到登录页
      } catch (error) {
        console.error('自动注销失败:', error);
        // 即使注销失败，也要清除本地状态
        authStore.$reset();
      }
    }
  }

  // 监听用户活动状态
  watch(
    idle,
    async (isIdle) => {
      // 只有在用户已登录时才处理超时
      if (!accessStore.accessToken) {
        return;
      }

      if (isIdle) {
        // 用户闲置超时，自动注销
        console.warn(`用户 ${timeoutMinutes} 分钟无操作，自动注销`, new Date());
        await autoLogout();
      } else {
        // 用户恢复活动，重置警告状态
        if (hasWarned) {
          // 用户恢复活动，重置会话超时警告
          hasWarned = false;

          // 关闭警告弹窗
          if (warningModal) {
            warningModal.destroy();
            warningModal = null;
          }
        }
      }
    },
    { immediate: false },
  );

  // 方案B：监听用户活动，在 Token 快过期时刷新
  // 使用节流控制，避免频繁检查
  let lastRefreshCheck = 0;
  const REFRESH_CHECK_INTERVAL = 10 * 1000; // 最多每10秒检查一次

  watch(
    lastActive,
    async () => {
      // 只有在用户已登录时才处理
      if (!accessStore.accessToken) {
        return;
      }

      // 节流：避免频繁检查
      const now = Date.now();
      if (now - lastRefreshCheck < REFRESH_CHECK_INTERVAL) {
        return;
      }
      lastRefreshCheck = now;

      // 检查并刷新 Token（如果需要）
      await doRefreshTokenIfNeeded();
    },
    { immediate: false },
  );

  // 定时器主动刷新：每分钟检查一次，不依赖用户活动事件
  // 这是关键修复：确保即使用户只是在看页面不动鼠标，Token 也能被及时刷新
  let tokenRefreshIntervalId: null | number = null;
  const TOKEN_REFRESH_INTERVAL = 60 * 1000; // 每分钟检查一次

  function startTokenRefreshTimer() {
    if (tokenRefreshIntervalId) {
      clearInterval(tokenRefreshIntervalId);
    }

    tokenRefreshIntervalId = window.setInterval(async () => {
      if (!accessStore.accessToken) {
        stopTokenRefreshTimer();
        return;
      }

      // 检查并刷新 Token（如果需要）
      await doRefreshTokenIfNeeded();
    }, TOKEN_REFRESH_INTERVAL);

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
 '%c[Token刷新] 定时器已启动（每分钟检查一次）',
        'color: #1890ff; font-weight: bold;',
      );
    }
  }

  function stopTokenRefreshTimer() {
    if (tokenRefreshIntervalId) {
      clearInterval(tokenRefreshIntervalId);
      tokenRefreshIntervalId = null;
    }
  }

  // 监听登录状态，启动/停止 Token 刷新定时器
  watch(
    () => accessStore.accessToken,
    (token, oldToken) => {
      if (token && !oldToken) {
        // 用户登录，启动定时器
        startTokenRefreshTimer();
      } else if (!token && oldToken) {
        // 用户注销，停止定时器
        stopTokenRefreshTimer();
      }
    },
    { immediate: true },
  );

  // 启动剩余时间更新定时器（每秒更新一次）
  function startUpdateTimer() {
    if (updateIntervalId) {
      clearInterval(updateIntervalId);
    }

    updateIntervalId = window.setInterval(() => {
      if (!accessStore.accessToken) {
        stopUpdateTimer();
        return;
      }
      currentTime.value = Date.now();
    }, 1000); // 每秒更新一次
  }

  // 停止剩余时间更新定时器
  function stopUpdateTimer() {
    if (updateIntervalId) {
      clearInterval(updateIntervalId);
      updateIntervalId = null;
    }
  }

  // 监听剩余时间，提前显示警告
  watch(remainingSeconds, (seconds) => {
    // 只有在用户已登录时才显示警告
    if (!accessStore.accessToken) {
      return;
    }

    // 当剩余时间等于警告时间时，显示警告
    const warningThreshold = warningMinutes * 60;

    if (seconds <= warningThreshold && seconds > 0 && !hasWarned) {
      console.warn(
        `会话将在 ${warningMinutes} 分钟后过期，显示警告`,
        new Date(),
      );
      showWarning();
    }
  });

  // 在开发环境输出调试信息
  /* eslint-disable no-console */
  if (import.meta.env.DEV) {
    console.log(
      '%c[会话超时] 检测已启用',
      'color: #52c41a; font-weight: bold;',
    );
    console.log(`  • 超时时间: ${timeoutMinutes} 分钟`);
    console.log(`  • 警告时间: ${warningMinutes} 分钟`);
    console.log(`  • 监听事件: 鼠标移动、键盘输入、点击、滚动等`);
    console.log(`  • 开始时间: ${new Date().toLocaleString()}`);

    // 监听用户登录状态，登录后开始输出倒计时
    watch(
      () => accessStore.accessToken,
      (token, oldToken) => {
        if (token && !oldToken) {
          // 用户刚登录
          console.log(
 '%c[会话超时] 用户已登录，开始计时',
            'color: #1890ff; font-weight: bold;',
          );
          console.log(
            `%c[会话超时] ✓ 初始剩余: ${formattedRemainingTime.value}`,
            'color: #52c41a;',
          );

          // 启动剩余时间更新定时器
          startUpdateTimer();

          // 清理旧的调试定时器
          if (debugIntervalId) {
            clearInterval(debugIntervalId);
          }

          // 每10秒输出一次剩余时间（仅开发环境，用于调试）
          debugIntervalId = window.setInterval(() => {
            if (!accessStore.accessToken) {
              if (debugIntervalId) {
                clearInterval(debugIntervalId);
                debugIntervalId = null;
              }
              return;
            }

            const minutes = Math.floor(remainingSeconds.value / 60);

            if (remainingSeconds.value <= warningMinutes * 60) {
              // 警告阶段：橙色（每10秒输出一次）
              console.log(
                `%c[会话超时] ⚠️ 剩余时间: ${formattedRemainingTime.value}`,
                'color: #fa8c16; font-weight: bold;',
              );
            } else if (minutes % 5 === 0 && remainingSeconds.value % 60 === 0) {
              // 每5分钟整点输出一次：绿色
              console.log(
                `%c[会话超时] ✓ 剩余时间: ${formattedRemainingTime.value}`,
                'color: #52c41a;',
              );
            } else {
              // 其他时间：灰色（每10秒输出）
              console.log(
 `%c[会话超时] ️ 剩余时间: ${formattedRemainingTime.value}`,
                'color: #8c8c8c;',
              );
            }
          }, 10_000); // 每10秒检查一次
        } else if (!token && oldToken) {
          // 用户注销
          console.log(
 '%c[会话超时] 用户已注销，停止计时',
            'color: #ff4d4f; font-weight: bold;',
          );

          // 停止剩余时间更新定时器
          stopUpdateTimer();

          // 清理调试定时器
          if (debugIntervalId) {
            clearInterval(debugIntervalId);
            debugIntervalId = null;
          }

          // 重置警告状态
          hasWarned = false;
          if (warningModal) {
            warningModal.destroy();
            warningModal = null;
          }
        }
      },
      { immediate: true }, // 立即执行，检查当前登录状态
    );
  } else {
    // 生产环境也需要启动更新定时器
    watch(
      () => accessStore.accessToken,
      (token, oldToken) => {
        if (token && !oldToken) {
          startUpdateTimer();
        } else if (!token && oldToken) {
          stopUpdateTimer();
        }
      },
      { immediate: true },
    );
  }
  /* eslint-enable no-console */

  // 创建清理函数
  const cleanup = () => {
    stopUpdateTimer();
    stopTokenRefreshTimer(); // 停止 Token 刷新定时器
    if (debugIntervalId) {
      clearInterval(debugIntervalId);
      debugIntervalId = null;
    }

    // 重置初始化标记（仅在开发环境，方便热更新）
    if (import.meta.env.DEV) {
      setInitialized(false);
      // eslint-disable-next-line no-console
      console.log(
 '%c[会话超时] 清理完成，已重置初始化标记',
        'color: #52c41a;',
      );
    } else {
      // eslint-disable-next-line no-console
 console.log('%c[会话超时] 清理完成', 'color: #52c41a;');
    }
  };

  // 监听登录状态，注销时自动清理
  watch(
    () => accessStore.accessToken,
    (token, oldToken) => {
      // 用户注销时清理资源
      if (!token && oldToken) {
        cleanup();
      }
    },
  );

  return {
    /** 用户是否处于闲置状态 */
    idle,
    /** 上次活动时间 */
    lastActive,
    /** 剩余秒数 */
    remainingSeconds,
    /** 格式化的剩余时间 */
    formattedRemainingTime,
  };
}

/**
 * 检查会话超时配置
 *
 * 用于验证环境变量配置是否正确
 *
 * @returns 配置检查结果
 *
 * @example
 * ```typescript
 * const { isValid, message, config } = validateSessionTimeoutConfig();
 * console.log(isValid, message, config);
 * ```
 */
export function validateSessionTimeoutConfig(): {
  config: {
    enabled: boolean;
    timeoutMinutes: number;
    warningMinutes: number;
  };
  isValid: boolean;
  message: string;
} {
  const enabled = import.meta.env.VITE_SESSION_TIMEOUT_ENABLED !== 'false';
  const timeoutMinutes =
    Number(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES) || 30;
  const warningMinutes =
    Number(import.meta.env.VITE_SESSION_WARNING_MINUTES) || 5;

  const config = {
    enabled,
    timeoutMinutes,
    warningMinutes,
  };

  // 验证配置
  if (!enabled) {
    return {
      isValid: true,
      message: '会话超时检测已禁用',
      config,
    };
  }

  if (timeoutMinutes <= 0) {
    return {
      isValid: false,
      message: '超时时间必须大于0分钟',
      config,
    };
  }

  if (warningMinutes <= 0) {
    return {
      isValid: false,
      message: '警告提前时间必须大于0分钟',
      config,
    };
  }

  if (warningMinutes >= timeoutMinutes) {
    return {
      isValid: false,
      message: '警告提前时间必须小于超时时间',
      config,
    };
  }

  return {
    isValid: true,
    message: '会话超时配置正常',
    config,
  };
}

/**
 * 启用会话超时检测
 *
 * @description 从后端加载配置并启动会话超时检测，如果加载失败则使用默认配置
 *
 * @important 只有在用户已登录时才会启动，未登录时会等待登录后自动启动
 *
 * @example
 * ```typescript
 * // 在应用启动时使用
 * import { useSessionTimeout } from '#/composables/useSessionTimeout';
 *
 * // 从后端加载配置并启动（自动检测登录状态）
 * await useSessionTimeout();
 * ```
 */
export async function useSessionTimeout() {
  // 防止重复初始化
  if (isInitialized()) {
    if (import.meta.env.DEV) {
      console.warn(
        '%c[会话超时] ⚠️ 已经初始化过，跳过重复初始化',
        'color: #faad14; font-weight: bold;',
      );
    }
    return;
  }

  // 标记为已初始化
  setInitialized(true);

  const accessStore = useAccessStore();

  // 检查用户是否已登录
  if (!accessStore.accessToken) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
        '%c[会话超时] 用户未登录，等待登录后启动',
        'color: #1890ff; font-weight: bold;',
      );
    }

    // 监听登录状态，登录后自动启动
    const unwatch = watch(
      () => accessStore.accessToken,
      async (token) => {
        if (token) {
          // 用户已登录，停止监听
          unwatch();

          try {
            // 从后端加载配置
            // 注意：getSessionTimeoutConfig 方法可能不存在，使用降级方案
            // const config = await systemConfigApi.getSessionTimeoutConfig();
            // 临时降级：使用默认配置
            const config = DEFAULT_SESSION_CONFIG;

            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log(
                '%c[会话超时] 用户登录后，使用后端配置启动',
                'color: #52c41a; font-weight: bold;',
                config,
              );
            }

            // 使用后端配置初始化
            initSessionTimeout(config);
          } catch (error) {
            console.error('[会话超时] 加载后端配置失败，使用默认配置:', error);

            // 降级：使用默认配置
            initSessionTimeout(DEFAULT_SESSION_CONFIG);
          }
        }
      },
      { immediate: false },
    );

    return;
  }

  // 用户已登录，直接启动
  try {
    // 从后端加载配置
    // 注意：getSessionTimeoutConfig 方法可能不存在，使用降级方案
    // const config = await systemConfigApi.getSessionTimeoutConfig();
    // 临时降级：使用默认配置
    const config = DEFAULT_SESSION_CONFIG;

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
        '%c[会话超时] 使用后端配置启动',
        'color: #52c41a; font-weight: bold;',
        config,
      );
    }

    // 使用后端配置初始化
    initSessionTimeout(config);
  } catch (error) {
    console.error('[会话超时] 加载后端配置失败，使用默认配置:', error);

    // 降级：使用默认配置
    initSessionTimeout(DEFAULT_SESSION_CONFIG);
  }
}
